import TemplatedExperienceAPI from 'services/api/templated-experience.api';
import { METRIC_OPTIONS, VISUALIZER_OPTIONS } from './CustomAnalytics/CreateOrEditChart/constants';
import {
  BarGraphData,
  ChartConfigState,
  ChartData,
  INTERVAL_OPTIONS,
  LineGraphData,
  ModuleConfig,
  PartialRelativeDateRange,
  RecommendedMetric,
  TableGraphData,
  TBarGraph,
  TLineGraph,
} from './types';
import moment from 'moment';
import { intlNumberFormat } from 'helpers/common.helper';

export const formatTotalValue = (value: number) => {
  if (value >= 1000000) {
    return intlNumberFormat(value, 'en-US', {
      notation: 'compact',
      compactDisplay: 'short', // Other option is 'long'
      maximumFractionDigits: 2,
    });
  }
  return intlNumberFormat(value);
};

export function isBarGraph(chartData: ChartData): chartData is TBarGraph {
  return chartData.moduleConfig.chartConfig.type === VISUALIZER_OPTIONS.BAR;
}

export function isLineGraph(chartData: ChartData): chartData is TLineGraph {
  return chartData.moduleConfig.chartConfig.type === VISUALIZER_OPTIONS.LINE;
}

// TODO: map chartConfig properly, and change the output to a serialization payload
export function serializeChartConfig(chartConfig: ChartConfigState, id?: string, tenantId?: string): ModuleConfig {
  const { title, description, metrics, visualizationType, timeFrame, selectedExperiences } = chartConfig;

  const relativeTimeOffset = timeFrame?.relative[0];
  const relativeTimeUnit = timeFrame?.relative[1];
  // FlightDateRangePicker sets relativeTimeOffset as `0` for `Today`
  // But, the API throws the following exception while saving the chart config
  // relativeTimeUnit and relativeTimeOffset is required for relative timeframe, timeInterval: hour, relativeTimeOffset: 0
  // Handles `relativeTimeOffset: 0` when users select `Today` in the `TimeFrameSelector` by setting it to `1` (TODO: make this better)
  const timeFramePayload =
    relativeTimeOffset !== null &&
    relativeTimeOffset !== undefined &&
    relativeTimeUnit !== null &&
    relativeTimeUnit !== undefined
      ? {
          type: 'relative',
          relativeTimeOffset: relativeTimeOffset === 0 ? 1 : relativeTimeOffset,
          relativeTimeUnit,
        }
      : {
          type: 'absolute',
          absoluteStart: moment(timeFrame?.start ?? 0).unix(),
          absoluteEnd: moment(timeFrame?.end ?? 0).unix(),
        };

  const moduleConfig = {
    id: id || '',
    tenantId: tenantId || '',
    name: title || '',
    description: description || '',
    timeFrame: {
      ...timeFramePayload,
      timeInterval: timeFrame?.period || INTERVAL_OPTIONS.DAILY,
      timeZone: 'America/Toronto',
      relativeTimeIsCurrentIncluded: true,
    },
    scope: {
      components: metrics?.entity || 'push',
      interactions: [metrics?.metric || 'viewed'],
      journeyIds: selectedExperiences?.map((experience) => experience.key) || [],
    },
    metrics: [
      {
        name: metrics?.title,
        components: metrics?.entity,
        interactions: [metrics?.metric],
        countDistinct: true,
        cumulativeOverTime: false,
      },
    ],
    chartConfig: {
      forceOverTime: true,
      type: visualizationType || VISUALIZER_OPTIONS.LINE,
      title: metrics?.title || '',
      xAxis: 'Time',
      yAxis: `${metrics?.entity}: ${metrics?.metric}`,
    },
    resultsConfig: {
      aggregateFunction: 'COUNT',
      maxResults: 5,
      sortOrder: 'DESC',
      countDistinct: false,
      cumulativeOverTime: false,
    },
  };

  return moduleConfig;
}

export async function deserializeModuleConfig(moduleConfig: ModuleConfig): Promise<ChartConfigState> {
  const templatedExperienceAPI = new TemplatedExperienceAPI();
  const { name, description, chartConfig, scope, metrics, timeFrame } = moduleConfig;
  const selectedExperiences = await Promise.all(
    scope.journeyIds?.map(async (id) => {
      try {
        const journey = await templatedExperienceAPI.getTemplatedExperienceInstance(id);
        return { key: journey.id, name: journey.name ?? '' };
      } catch (e) {
        return { key: id, name: 'Unknown Experience' };
      }
    }) ?? [],
  );

  const customDateRange: Date[] = [];
  // Default to an empty relative tuple so saved `absolute` (Custom) charts remain distinguishable
  // from `relative` (preset) charts on reload — the label logic relies on this discriminator.
  let relativeDateRange: PartialRelativeDateRange = [undefined, undefined];
  if (timeFrame.type === 'absolute') {
    customDateRange.push(new Date(timeFrame.absoluteStart * 1000), new Date(timeFrame.absoluteEnd * 1000));
  } else {
    relativeDateRange = [timeFrame.relativeTimeOffset, timeFrame.relativeTimeUnit];
  }

  // Single metric only
  const metric = metrics[0] || {};
  const metricTitle = metric.name || chartConfig.title;
  const metricEntity = metric.components?.split('|')?.[0] || scope.components?.split('|')?.[0] || 'push';
  const metricType = metric.interactions[0] || scope.interactions[0] || 'viewed';

  const config = {
    title: name,
    description,
    selectedExperiences,
    metrics: {
      title: metricTitle,
      entity: metricEntity.trim(),
      metric: metricType.trim(),
    },
    timeFrame: {
      start: customDateRange[0],
      end: customDateRange[1],
      period: timeFrame.timeInterval,
      relative: relativeDateRange,
    },
    visualizationType: chartConfig.type ?? VISUALIZER_OPTIONS.BAR,
  };

  return config;
}

export function mapRecommendationToChartConfig(
  recommendation: RecommendedMetric | undefined | null,
  chartConfig: ChartConfigState,
): ChartConfigState {
  if (!recommendation) {
    return chartConfig;
  }

  const {
    metric: { key },
    time: { period, interval },
    visualization,
  } = recommendation;

  const metricOption = METRIC_OPTIONS.find((option) => option.key === key);
  const metricValue = metricOption?.value || { title: '', entity: '', metric: '' };
  const { title, entity, metric } = metricValue;

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  return {
    ...chartConfig,
    metrics: {
      title,
      entity,
      metric,
    },
    timeFrame: {
      start: null,
      end: null,
      period: INTERVAL_OPTIONS.DAILY,
      relative: [period, interval],
    },
    visualizationType: visualization,
  };
}

export function generateGraphData(type?: VISUALIZER_OPTIONS | null, timeframe?: ChartConfigState['timeFrame']) {
  switch (type) {
    case VISUALIZER_OPTIONS.LINE:
      return generateLineGraphData(timeframe);
    case VISUALIZER_OPTIONS.TABLE:
      return generateTableData(timeframe);
    case VISUALIZER_OPTIONS.BAR:
      return generateBarGraphData(timeframe);
    default:
      return [];
  }
}
export function getDaysBetweenDates(startDate: Date, endDate: Date): number {
  const oneDay = 24 * 60 * 60 * 1000; // milliseconds in a day
  const diffInTime = endDate.getTime() - startDate.getTime();
  return Math.round(diffInTime / oneDay);
}
// TODO: check if the timeframe should affect the preview
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function generateLineGraphData(timeframe?: ChartConfigState['timeFrame']) {
  const data: LineGraphData = [];
  const yValues = Array.from({ length: 10 }, () => Math.random() * 100).sort((a, b) => a - b);

  const startTime = Date.now();
  const oneDay = 24 * 60 * 60 * 1000;

  for (let i = 0; i < 10; i++) {
    const point = {
      name: 'data',
      x: startTime + i * oneDay,
      y: yValues[i],
    };
    data.push(point);
  }

  return data;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function generateBarGraphData(timeframe?: ChartConfigState['timeFrame']) {
  const data: BarGraphData = [];
  const yValues = Array.from({ length: 10 }, () => Math.random() * 100).sort((a, b) => a - b);

  const startTime = Date.now();
  const oneDay = 24 * 60 * 60 * 1000;

  for (let i = 0; i < 10; i++) {
    const point = {
      journey_id: 'data',
      name: new Date(startTime + i * oneDay * 1000).getDate().toString(),
      x: startTime + i * oneDay,
      value: yValues[i],
      y: yValues[i],
    };
    data.push(point);
  }

  return data;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function generateTableData(timeframe?: ChartConfigState['timeFrame']) {
  const data: TableGraphData = [];
  const values = Array.from({ length: 10 }, () => Math.random() * 100).sort((a, b) => a - b);

  const startTime = Date.now();
  const oneDay = 24 * 60 * 60 * 1000;

  for (let i = 0; i < 10; i++) {
    const point = {
      journey_id: 'data',
      name: new Date(startTime + i * oneDay).toDateString(),
      value: values[i],
    };
    data.push(point);
  }

  return data;
}
